import React, { useEffect, useMemo, useState } from 'react';

import { Form as FinalForm, Field } from 'react-final-form';
import { connect } from 'react-redux';

import {
    Button,
    FormCheckbox,
    Spinner,
} from 'components';

import FormInputField from 'components/FormInputField/FormInputField';
import FormDropdownField from 'components/FormDropDownField/FormDropDownField';
import EmailTemplatePreview from 'components/EmailTemplatePreview/EmailTemplatePreview';

import { actions as locationActions } from 'coupon-common/src/modules/locations';
import { actions as managePointsActions } from 'coupon-common/src/modules/admin/pointsManager/managePoints';
import { actions as emailActions } from 'coupon-common/src/modules/admin/emailManager';

import styles from './SendEmail.module.scss';
import SendTestEmailModal from 'components/SendTestEmailModal/SendTestEmailModal';

const RECIPIENT_TYPES = {
    ALL_USERS: 'all_users',
    CUSTOMERS: 'customers',
    BUSINESSES: 'businesses',
    USERS_BY_FOLLOWED_BUSINESS: 'users_by_followed_business',
    MANUAL_EMAIL: 'manual_email',
};

const recipientOptions = [
    {
        value: RECIPIENT_TYPES.ALL_USERS,
        label: 'All Users',
    },
    {
        value: RECIPIENT_TYPES.CUSTOMERS,
        label: 'Customers',
    },
    {
        value: RECIPIENT_TYPES.BUSINESSES,
        label: 'Businesses',
    },
    {
        value: RECIPIENT_TYPES.USERS_BY_FOLLOWED_BUSINESS,
        label: 'Users by Followed Business',
    },
    {
        value: RECIPIENT_TYPES.MANUAL_EMAIL,
        label: 'Manual Email',
    },
];

const initialValues = {
    recipientType: null,
    provinces: [],
    cities: [],
    business: null,
    manualEmails: [],
    manualEmailsInput: '',
    subject: '',
    body: '',
    useBaseTemplate: true,
};

const getResults = data => {
    if (Array.isArray(data)) {
        return data;
    }

    if (data?.results) {
        return data.results;
    }

    return Object.values(data || {});
};

const SendEmail = ({
    dispatch,
    cities,
    provinces,
    usersData,
    templates,
}) => {
    const [showPreview, setShowPreview] = useState(false);
    const [previewValues, setPreviewValues] = useState(null);
    const [showTestEmail, setShowTestEmail] = useState(false);
    const [loading, setLoading] = useState(false);

    // useEffect(() => {
    //     dispatch(locationActions.getProvinces());
    //     dispatch(locationActions.getCities());
    //     dispatch(emailActions.getEmailTemplates());

    //     if (!usersData?.length) {
    //         dispatch(managePointsActions.getAllUsers());
    //     }
    // }, [dispatch]);

    useEffect(() => {
    if (!provinces?.length) {
        dispatch(locationActions.getProvinces());
    }

    if (!cities?.length) {
        dispatch(locationActions.getCities());
    }

    if (!templates?.length) {
        dispatch(emailActions.getEmailTemplates());
    }

    if (!usersData?.length) {
        dispatch(managePointsActions.getAllUsers());
    }
}, [dispatch]);

    const provinceOptions = useMemo(
        () => getResults(provinces),
        [provinces],
    );

    const cityOptions = useMemo(
        () => getResults(cities),
        [cities],
    );

    const businessOptions = useMemo(() => {
        const users = getResults(usersData);

        return users
            .filter(user => user.is_business)
            .map(user => ({
                value: user.id,
                label: user.email,
            }));
    }, [usersData]);

    const baseTemplate = useMemo(
        () =>
            templates.find(
                template =>
                    template.name ===
                    'BASE_EMAIL_TEMPLATE'
            ),
        [templates],
    );

    const buildPreviewHtml = values => {
        let html = values?.body || '';

        if (
            values?.useBaseTemplate &&
            baseTemplate
        ) {
            html = baseTemplate.body.replace(
                '{content}',
                html
            );
        }

        return html;
    };

    const handlePreview = values => {
        setPreviewValues(values);
        setShowPreview(true);
    };

    const handleSend = (values, form) => {
        setLoading(true);

        const payload = {
            recipientType: values.recipientType,
            ...(values.provinces?.length && {
                provinces: values.provinces,
            }),
            ...(values.cities?.length && {
                cities: values.cities,
            }),
            ...(values.business?.length && {
                business: values.business,
            }),
            ...(values.manualEmails?.length && {
                manualEmails: values.manualEmails,
            }),
            subject: values.subject,
            body: buildPreviewHtml(values),
        };

        dispatch(
            emailActions.sendEmail(
                payload,
                result => {
                    if (result.success) {
                        alert(
                            `Email sending has been started successfully to ${result.data.recipient_count} recipient(s).`,
                        );

                        setPreviewValues(null);
                        setShowPreview(false);
                        form.reset();
                    } else {
                        alert(
                            result.error ||
                            'Failed to send email.',
                        );
                    }

                    setLoading(false);
                },
            ),
        );
    };

    const handleManualEmailsChange = (
        value,
        form
    ) => {
        const emails = value
            .split(',')
            .map(email => email.trim())
            .filter(Boolean);

        form.change('manualEmails', emails);
        form.change(
            'manualEmailsInput',
            value
        );
    };

    return (
        <div className={styles.container}>
            <h4>Send Email</h4>

            <FinalForm
                initialValues={initialValues}
                onSubmit={handleSend}
                render={({
                    handleSubmit,
                    values,
                    submitting,
                    form,
                }) => {
                    const hasRecipientType =
                        !!values.recipientType;

                    const showManualEmails =
                        values.recipientType ===
                        RECIPIENT_TYPES.MANUAL_EMAIL;

                    const showBusiness =
                        values.recipientType ===
                        RECIPIENT_TYPES.USERS_BY_FOLLOWED_BUSINESS;

                    const hasProvince =
                        values.provinces?.length > 0;

                    const filteredCities =
                        hasProvince
                            ? cityOptions.filter(
                                city =>
                                    values.provinces.some(
                                        province =>
                                            province.value ===
                                            city.province,
                                    ),
                            )
                            : [];

                    return (
                        <>
                            <form onSubmit={handleSubmit}>
                                <div
                                    className={
                                        styles.formGroup
                                    }
                                >
                                    <FormDropdownField
                                        name="recipientType"
                                        label="Recipient Type"
                                        placeholder="Select recipient type"
                                        options={
                                            recipientOptions
                                        }
                                        searchable
                                    />
                                </div>

                                {showBusiness && (
                                    <div
                                        className={
                                            styles.formGroup
                                        }
                                    >
                                        <FormDropdownField
                                            name="business"
                                            label="Business"
                                            placeholder="Select business"
                                            options={
                                                businessOptions
                                            }
                                            searchable
                                            isMulti
                                        />
                                    </div>
                                )}

                                {hasRecipientType &&
                                    !showManualEmails &&
                                    !showBusiness && (
                                        <div
                                            className={
                                                styles.formGroup
                                            }
                                        >
                                            <FormDropdownField
                                                name="provinces"
                                                label="Provinces (Optional)"
                                                placeholder="Select provinces to filter recipients"
                                                options={
                                                    provinceOptions
                                                }
                                                searchable
                                                isMulti
                                                mapOption={province => ({
                                                    value:
                                                        province.id,
                                                    label:
                                                        province.name,
                                                })}
                                            />

                                            <small
                                                className={
                                                    styles.fieldHelp
                                                }
                                            >
                                                Leave blank to
                                                include
                                                recipients from
                                                all provinces.
                                            </small>
                                        </div>
                                    )}

                                {hasProvince &&
                                    !showManualEmails &&
                                    !showBusiness && (
                                        <div
                                            className={
                                                styles.formGroup
                                            }
                                        >
                                            <FormDropdownField
                                                name="cities"
                                                label="Cities (Optional)"
                                                placeholder="Select cities to further filter recipients"
                                                options={
                                                    filteredCities
                                                }
                                                searchable
                                                isMulti
                                                mapOption={city => ({
                                                    value:
                                                        city.id,
                                                    label:
                                                        city.name,
                                                })}
                                            />

                                            <small
                                                className={
                                                    styles.fieldHelp
                                                }
                                            >
                                                Leave blank to
                                                include
                                                recipients from
                                                all selected
                                                provinces.
                                            </small>
                                        </div>
                                    )}

                                {showManualEmails && (
                                    <div
                                        className={
                                            styles.formGroup
                                        }
                                    >
                                        <Field name="manualEmailsInput">
                                            {({ input }) => (
                                                <>
                                                    <label htmlFor="manual-emails">
                                                        Manual Email
                                                        Addresses
                                                    </label>

                                                    <textarea
                                                        {...input}
                                                        id="manual-emails"
                                                        rows={3}
                                                        className="form-control"
                                                        placeholder="Enter email addresses separated by commas"
                                                        disabled={
                                                            submitting ||
                                                            loading
                                                        }
                                                        onChange={e =>
                                                            handleManualEmailsChange(
                                                                e.target
                                                                    .value,
                                                                form,
                                                            )
                                                        }
                                                    />
                                                </>
                                            )}
                                        </Field>
                                    </div>
                                )}

                                <FormInputField
                                    name="subject"
                                    label="Subject"
                                    placeholder="Enter email subject"
                                    required
                                />

                                <div className="mb-3">
                                    <Field
                                        name="useBaseTemplate"
                                        type="checkbox"
                                    >
                                        {({
                                            input,
                                            meta,
                                        }) => (
                                            <FormCheckbox
                                                label="Use Base Email Template"
                                                input={input}
                                                meta={meta}
                                            />
                                        )}
                                    </Field>
                                </div>

                                <div
                                    className={
                                        styles.formGroup
                                    }
                                >
                                    <Field name="body">
                                        {({ input }) => (
                                            <>
                                                <label htmlFor="email-body">
                                                    Body
                                                </label>

                                                <textarea
                                                    {...input}
                                                    id="email-body"
                                                    rows={14}
                                                    className="form-control"
                                                    placeholder="Enter email content"
                                                    disabled={
                                                        submitting ||
                                                        loading
                                                    }
                                                />
                                            </>
                                        )}
                                    </Field>
                                </div>

                                <div
                                    className={
                                        styles.actions
                                    }
                                >
                                    <Button
                                        outline
                                        type="button"
                                        onClick={() =>
                                            handlePreview(
                                                values
                                            )
                                        }
                                        disabled={
                                            loading ||
                                            submitting ||
                                            !values.subject ||
                                            !values.body
                                        }
                                    >
                                        Preview
                                    </Button>

                                    <Button
                                        outline
                                        type="button"
                                        onClick={() =>
                                            setShowTestEmail(
                                                true,
                                            )
                                        }
                                        disabled={
                                            loading ||
                                            submitting ||
                                            !values.subject ||
                                            !values.body
                                        }
                                    >
                                        Send Test Email
                                    </Button>

                                    <Button
                                        type="submit"
                                        disabled={
                                            loading ||
                                            submitting ||
                                            !values.subject ||
                                            !values.body
                                        }
                                    >
                                        {loading ? (
                                            <Spinner
                                                show={loading}
                                            />
                                        ) : (
                                            'Send Email'
                                        )}
                                    </Button>
                                </div>
                            </form>

                            <SendTestEmailModal
                                isOpen={showTestEmail}
                                onRequestClose={() =>
                                    setShowTestEmail(false)
                                }
                                subject={
                                    values.subject
                                }
                                body={buildPreviewHtml(
                                    values
                                )}
                            />
                        </>
                    );
                }}
            />

            {showPreview &&
                previewValues && (
                    <EmailTemplatePreview
                        subject={
                            previewValues.subject
                        }
                        html={buildPreviewHtml(
                            previewValues
                        )}
                        onHide={() =>
                            setShowPreview(false)
                        }
                    />
                )}
        </div>
    );
};

const mapStateToProps = state => ({
    provinces:
        state.locations.entities.provinces || [],
    cities:
        state.locations.entities.cities || [],
    usersData:
        state.admin.pointsManager.managePoints
            .getUsers.data || [],
    templates:
        state.admin.emailManager?.list?.data || [],
});

export default connect(mapStateToProps)(SendEmail);